<template>
  <div class="content-detail-wrapper tw-pt-[25px]">
    <div class="cyber-insurance-policy">
      <ActionsOverviewDescription :data="data" />
    </div>
    <div class="score-risk-wrapper-action">
      <div
        class="score-action tw-border-solid tw-border tw-border-[#EFF0F1] tw-rounded-md tw-text-[#211F58]"
      >
        <CyberHealthScore
          :data="dataScore"
          :score="{
            currentSCore: data.item.scoreIncreaseFirstCount,
            maxScore: data.item.scoreIncreaseTotal,
          }"
          type="scoreIncrease"
        >
          <template #header>
            <div
              class="tw-h-[56px] tw-border-solid tw-border-0 tw-border-b tw-border-[#EFF0F1] tw-p-[20px] tw-flex tw-items-center tw-justify-between tw-bg-[#FCFCFD] tw-rounded-tl-md tw-rounded-tr-md"
            >
              <div
                class="tw-text-[15px] tw-font-semibold tw-w-full tw-text-[#211F58] tw-flex tw-items-center tw-gap-2 tw-leading-[15px]"
              >
                <ScoreIcon :width="12" :height="12" color="#211F58" />
                {{
                  getComponentUiText(
                    "app-assess-recommendations-action-table-label-score-1",
                  )
                }}
              </div>
            </div>
          </template>
        </CyberHealthScore>
      </div>
      <div class="risk-action">
        <Risk
          :list-risk="[listRisk[1], listRisk[2]]"
          :type-style="true"
          :is-action-table="data?.item?.checklistGroupId"
        >
          <div class="tw-flex">
            <div class="tw-mr-[8px]">
              <tileRiskIcon />
            </div>
            <div
              style="line-height: 24px"
              class="!tw-text-[15px] tw-font-semibold tw-w-full tw-text-[#211F58] tw-bg-[#fcfcfd]"
            >
              {{
                getComponentUiText("app-assess-recommendations-title-risk-1")
              }}
            </div>
          </div>
        </Risk>
      </div>
    </div>
    <div class="table-overview">
      <AssessmentQuestionsTable
        :draggable="true"
        :loading="isLoadingTableQuestion"
        :table-data="dataSuggestedActionTable"
        :search-key="''"
        :column="['category', 'orderNumber', 'totalScore', 'action']"
        :in-tab-overview="true"
        :is-action-table="isActionTable"
        :checklist-group-id="data.item.checklistGroupId"
      />
    </div>
    <div
      v-if="data?.item?.checklistGroupName"
      class="tw-text-[13px] tw-text-[#878787] tw-text-right tw-font-normal"
    >
      {{
        getComponentUiText(
          "app-assess-recommendations-action-create-from-suggested-1",
        )
      }}
      {{ data.item.checklistGroupName }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import CyberHealthScore from "./cyberHealthScore.vue";
import ScoreIcon from "@/components/svgs/scoreIcon.vue";
import { getChecklistGroupData, getComponentUiText } from "@/utils/utilities";
import ActionsOverviewDescription from "./actionsOverviewDescription.vue";
import Risk from "@/components/recommend/risk.vue";
import tileRiskIcon from "@/components/svgs/titleRiskIcon.vue";
import store from "@/store";
import { computed, ref } from "vue";
import { onMounted } from "vue";
import { IToastMessage, ToastVariant } from "@/interfaces/common/IToastMessage";
import EventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import {
  getDataChecklistGroup,
  getDataChecklistGroupSuggestedActions,
} from "@/services/recommend";
import { RISK_VALUE_MAP, successStatusCodes } from "@/utils/constants";
import { watch } from "vue";
import { IAssessmentQuestionTable } from "@/interfaces/assessments/IAssessmentDetails";
import AssessmentQuestionsTable from "@/components/assessment/assessmentQuestionsTable.vue";

const props = defineProps({
  data: {
    type: Object as () => any,
    required: false,
    default: null,
  },
  idItemExpand: {
    type: [String, Number],
    required: false,
    default: null,
  },
  isAction: {
    type: Boolean,
    required: false,
    default: false,
  },
  isActionTable: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const dataSuggestedActionTable = ref<any>([]);
const isLoadingTableQuestion = ref(true);

const checklistId = computed(
  () => store.getters["Recommend/getChecklistReportId"],
);

const checklistAssessmentQuestion = computed(() =>
  store.getters["Recommend/getChecklistAssessmentQuestions"](false),
);

// watch(checklistId, () => {
//   getChecklistGroupData();
// });

watch(checklistAssessmentQuestion, () => {
  if (props.idItemExpand === props.data?.item.id) {
    fetchChecklistGroupData();
  }
});

watch(
  () => props.idItemExpand,
  (newVal, oldVal) => {
    if (newVal === props.data?.item.id) {
      fetchChecklistGroupData();
    }
  },
  { deep: true },
);

onMounted(() => {
  if (props.idItemExpand === props.data?.item.id) {
    fetchChecklistGroupData();
  }
  processRiskData();
  processRiskStatus();
});

const fetchChecklistGroupData = async () => {
  isLoadingTableQuestion.value = true;
  try {
    if (
      !checklistId.value ||
      (!props.data?.item?.checklistGroupId &&
        !props.data?.item?.checklistGroupTemplateId) ||
      !checklistAssessmentQuestion.value ||
      !checklistAssessmentQuestion.value.length
    ) {
      return;
    }

    const res = props.data?.item?.checklistGroupId
      ? await getDataChecklistGroup(
          checklistId.value,
          props.data?.item?.checklistGroupId,
        )
      : await getDataChecklistGroupSuggestedActions(
          checklistId.value,
          props.data?.item?.checklistGroupTemplateId,
        );
    if (res.status !== successStatusCodes.OK) {
      return;
    }
    const checklistItems = res.data?.data?.checklistItems || [];

    dataSuggestedActionTable.value = getChecklistGroupData(
      checklistAssessmentQuestion.value,
      checklistItems,
    );
  } catch (error: any) {
    const message = error?.response?.data?.message || "";
    const emitData: IToastMessage = {
      variant: ToastVariant.ERROR,
      message,
    };
    EventEmitter.emit(EmitterEvents.APP_LAYOUT_SHOW_TOAST_MESSAGE, emitData);
  } finally {
    isLoadingTableQuestion.value = false;
  }
};

const listRisk = ref([
  {
    text: getComponentUiText("app-assess-recommendations-current-risk-1"),
    lowNumber: 1,
    mediumNumber: 2,
    highNumber: 15,
    bgLow: "#57B755",
    bgHigh: "#F7553C",
    bgMedium: "#FE9A05",
    colorHigh: "#F7553C",
    bgText: "#FEE5E2",
    textStatus: getComponentUiText("app-assess-recommendations-high-risk-1"),
    colorText: "#F7553C",
    isReduction: true,
  },
  {
    text: getComponentUiText("app-assess-recommendations-potential-risk-1"),
    lowNumber: 0,
    mediumNumber: 0,
    highNumber: 0,
    bgLow: "#57B755",
    bgHigh: "#F7553C",
    bgMedium: "#FE9A05",
    colorHigh: "#F7553C",
    bgText: "#D9EDE2",
    textStatus: getComponentUiText("app-assess-recommendations-low-risk-1"),
    colorText: "#01893D",
    isReduction: true,
  },
  {
    text: getComponentUiText(
      "app-assess-recommendations-action-table-header-relative-risk-1",
    ),
    isRelativeRisk: true,
    value: props.data?.item?.relativeRiskValue,
    textStatus: "Significant",
    colorText: "#fff",
    bgText: "#904BD6",
    isReduction: true,
  },
]);
const dataScore = [
  {
    title: getComponentUiText(
      "app-assess-recommendations-cyber-health-score-sub-title-1",
    ),
    id: "currentScore",
    value: 0.37,
  },
  {
    title: getComponentUiText(
      "app-assess-recommendations-cyber-health-score-sub-title-4",
    ),
    id: "maxScore",
    value: 2,
  },
];

const processRiskData = () => {
  if (!props.data.item || !props.data.item.riskDetails) {
    return;
  }
  let lowNumber = 0;
  let mediumNumber = 0;
  let highNumber = 0;
  props.data.item.riskDetails.forEach((risk) => {
    if (risk.riskValue > RISK_VALUE_MAP.HIGH) {
      highNumber++;
    } else if (risk.riskValue > RISK_VALUE_MAP.MEDIUM) {
      mediumNumber++;
    } else if (risk.riskValue > RISK_VALUE_MAP.LOW) {
      lowNumber++;
    }
  });
  listRisk.value[1].lowNumber = lowNumber;
  listRisk.value[1].mediumNumber = mediumNumber;
  listRisk.value[1].highNumber = highNumber;
};

const processRiskStatus = () => {
  const { riskProbabilityLevelValueMax, riskImpactLevelValueMax } =
    props.data.item;

  const result = riskProbabilityLevelValueMax * riskImpactLevelValueMax;

  if (result > RISK_VALUE_MAP.HIGH) {
    listRisk.value[1].textStatus = getComponentUiText(
      "app-assess-recommendations-high-risk-1",
    );
    listRisk.value[1].bgText = "#FEE5E2";
    listRisk.value[1].colorText = "#F7553C";
  } else if (result > RISK_VALUE_MAP.MEDIUM) {
    listRisk.value[1].textStatus = getComponentUiText(
      "app-assess-recommendations-medium-risk-1",
    );
    listRisk.value[1].bgText = "#FFF0DA";
    listRisk.value[1].colorText = "#FE9A05";
  } else if (result > RISK_VALUE_MAP.LOW) {
    listRisk.value[1].textStatus = getComponentUiText(
      "app-assess-recommendations-low-risk-1",
    );
    listRisk.value[1].bgText = "#D9EDE2";
    listRisk.value[1].colorText = "#01893D";
  }
};

watch(
  () => props.data,
  () => {
    processRiskData();
    processRiskStatus();
  },
);
</script>

<style lang="scss" scoped>
.content-detail-wrapper {
  width: 100%;
  display: flex;
  gap: 25px;
  flex-direction: column;
  height: auto;
}
.cyber-insurance-policy {
  width: 100%;
  // height: 151px;
  // background-color: silver;
}
.score-risk-wrapper-action {
  display: flex;
  gap: 25px;
  width: 100%;
}
.score-action {
  width: 100%;
}
.risk-action {
  width: 100%;
  border-radius: 6px;
  border: 1px solid var(--UI-rule, #eff0f1);
  background: var(--UI-white, #fff);
  height: 152px;
}

.table-overview {
  width: 100%;
}
</style>
